import {
  Cell,
  Column,
  ColumnProps,
  Pill,
  Row,
  Table,
  TableBody,
  TableFooter,
  TableHeader,
} from '@eleveo/components-react';
import { CheckmarkIcon, WarningIcon } from '@eleveo/icons-react';
import { format } from 'date-fns';
import React from 'react';
import { useTranslation } from 'react-i18next';

import { TABLE_TAG } from './constants';
import { useAuth } from '../../app/AuthorizationProvider';
import { DataImportHistoryRecord, ImportTypeEnum } from '../../graphql/components';

type DataImportListProps = { data: DataImportHistoryRecord[]; pagination: React.ReactNode };

type ColumnType = {
  align: ColumnProps<any>['align'];
  key: string;
  name: string;
};

export const getImportType = (type: ImportTypeEnum) => {
  switch (type) {
    case ImportTypeEnum.Auto:
      return 'dataImport.type.auto';
    case ImportTypeEnum.Manual:
      return 'dataImport.type.manual';
    default:
      return 'dataImport.unspecified';
  }
};

export const DataImportTable: React.FC<DataImportListProps> = ({ data, pagination }) => {
  const { localeObj } = useAuth();
  const { t } = useTranslation();

  const success = (
    <Pill color="success">
      <CheckmarkIcon />
      {t('dataImport.status.success')}
    </Pill>
  );
  const failed = (
    <Pill color="error">
      <WarningIcon />
      {t('dataImport.status.failed')}
    </Pill>
  );

  const columns: ColumnType[] = [
    { align: 'start', key: 'status', name: t('dataImport.status.status') },
    { align: 'start', key: 'dateOfImport', name: t('dataImport.dateOfImport') },
    { align: 'start', key: 'source', name: t('dataImport.source') },
    { align: 'start', key: 'importType', name: t('dataImport.type.importType') },
  ];

  return (
    <Table aria-label={t('dataImport.dataImport')} isStickyHeader testId={TABLE_TAG}>
      <TableHeader columns={columns}>
        {(column) => (
          <Column align={column.align} key={column.key}>
            {column.name}
          </Column>
        )}
      </TableHeader>

      <TableBody items={data}>
        {(dataRecord) => (
          <Row>
            {(columnKey) => {
              switch (columnKey) {
                case 'status':
                  return <Cell>{dataRecord.status === 0 ? success : failed}</Cell>;
                case 'dateOfImport':
                  return (
                    <Cell>
                      <strong>
                        {format(new Date(dataRecord.dateOfImport), t('dataImport.dateOfImportFormat'), {
                          locale: localeObj,
                        })}
                      </strong>
                    </Cell>
                  );
                case 'source':
                  return <Cell>{dataRecord.source}</Cell>;
                case 'importType':
                  return <Cell>{t(getImportType(dataRecord.importType))}</Cell>;
                default:
                  return <Cell>&nbsp;</Cell>;
              }
            }}
          </Row>
        )}
      </TableBody>
      <TableFooter>{pagination}</TableFooter>
    </Table>
  );
};
